import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '../../services/app.service';
import { HttpService } from '../../services/http.service';
import { PermissionService } from '../../services/permission.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { UrlService } from '../../services/url.service';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

const EMAIL_SEPERATOR = /[,;]/; // separate by "," or ";"

function commaSeparatedEmailsValidator(control: AbstractControl): ValidationErrors {
    const errors = (<string>control.value)
        .split(EMAIL_SEPERATOR)
        .map((email) => Validators.email(<AbstractControl>{ value: email.trim() }))
        .filter((v) => v !== null);
    if (errors.length) {
        return { email: true };
    }
    return null;
}

@Component({
    selector: 'app-send-documents-via-email-dialog',
    templateUrl: './send-documents-via-email-dialog.component.html',
    styleUrls: ['./send-documents-via-email-dialog.component.scss'],
})
export class SendDocumentsViaEmailDialogComponent implements OnInit {
    public sendEmailForm = new FormGroup({
        email: new FormControl('', [Validators.required, commaSeparatedEmailsValidator]),
        emailCC: new FormControl('', [commaSeparatedEmailsValidator]),
        emailBCC: new FormControl('', [commaSeparatedEmailsValidator]),
        emailBody: new FormControl(''),
    });
    public isLoading = false;
    public sanitizedHtml: SafeHtml;
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: SendDocumentSettings,
        public dialogRef: MatDialogRef<SendDocumentsViaEmailDialogComponent>,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private permissionService: PermissionService,
        private urlService: UrlService,
        private appService: AppService,
        private httpService: HttpService,
        private sanitizer: DomSanitizer
    ) {}

    public ngOnInit() {
        if (this.permissionService.userInfo$.value && this.permissionService.userInfo$.value.mail) {
            const prefillControl = this.permissionService.isUserAnEmployee
                ? this.sendEmailForm.controls.emailCC
                : this.sendEmailForm.controls.email;
            prefillControl.setValue(this.permissionService.userInfo$.value.mail);
        }
        this.sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(this.data.mailText || '');
    }

    public onNoClick() {
        this.dialogRef.close();
    }

    public onYesClick() {
        this.isLoading = true;
        this.sendMail();
    }

    private setControlsEnabled(enabled: boolean) {
        Object.values(this.sendEmailForm.controls).forEach((control) => {
            if (enabled) {
                control.enable();
            } else {
                control.disable();
            }
        });
    }

    private getExportFormats() {
        const formats = [];
        if (this.data.uglSelected) {
            formats.push('UGL');
        }

        if (this.data.gaebD83Selected) {
            formats.push('GAEB_D83');
        }

        if (this.data.gaebX94Selected) {
            formats.push('GAEBXML_X94');
        }

        return formats;
    }

    private sendMail() {
        this.setControlsEnabled(false);
        const exportFormats = this.getExportFormats();
        const { email, emailCC, emailBCC, emailBody } = this.sendEmailForm.value as {
            email: string;
            emailCC: string;
            emailBCC: string;
            emailBody: string;
        };
        const toEmailList = (mail: string) =>
            mail
                .split(EMAIL_SEPERATOR)
                .map((v) => v.trim())
                .filter((v) => !!v);
        this.sendMailWithDocuments({
            documentId: this.data.documentId,
            latestRevision: this.data.latestRevision,
            schemes: this.data.schemes,
            energyLabel: this.data.energyLabel,
            exportFormats,
            emails: toEmailList(email),
            emailsCC: toEmailList(emailCC),
            emailsBCC: toEmailList(emailBCC),
            emailBody,
            offerAttachments: this.data.offerAttachments,
            printType: this.data.printType,
        }).subscribe(
            () => {
                this.snackBarService.openSnackBar({
                    message: this.translateService.instant('NEXT_STEPS.EMAIL_DIALOG.SENDING_DOCUMENTS.SUCCESS_MSG'),
                    isFailure: false,
                });
                this.dialogRef.close();
                this.isLoading = false;
                this.setControlsEnabled(true);
            },
            () => {
                this.snackBarService.openSnackBar({
                    message: this.translateService.instant('NEXT_STEPS.EMAIL_DIALOG.SENDING_DOCUMENTS.FAILURE_MSG'),
                    isFailure: true,
                });
                this.isLoading = false;
                this.setControlsEnabled(true);
            }
        );
    }

    public sendMailWithDocuments({
        documentId,
        latestRevision,
        schemes,
        energyLabel,
        exportFormats,
        emails,
        emailsCC,
        emailsBCC,
        emailBody,
        offerAttachments,
        printType,
    }: {
        documentId: string;
        latestRevision: string;
        schemes: string[];
        energyLabel: string;
        exportFormats: Array<string>;
        emails: Array<string>;
        emailsCC: Array<string>;
        emailsBCC: string[];
        emailBody: string;
        offerAttachments: any[];
        printType: string;
    }) {
        const url = this.urlService.getUrl('sendMailWithDocuments', {
            documentId,
            latestRevision,
        });

        const payload = {
            emailReceivers: emails,
            emailCCReceivers: emailsCC,
            emailBCCReceivers: emailsBCC,
            emailBody,
            sendEmailToSalesRep: false,
            exportFormats,
            language: this.appService.getLanguageOnly(),
            country: this.appService.getLocation(),
            salesOrganization: this.appService.getSalesOrg(),
            schemes,
            ...(this.data.energyLabelSelected && { energyLabel }),
            offerAttachments,
            printType,
        };

        return this.httpService.post(url, payload);
    }
}

export interface SendDocumentSettings {
    uglSelected: boolean;
    gaebD83Selected: boolean;
    gaebX94Selected: boolean;
    energyLabelSelected: boolean;
    energyLabel: string;
    schemes: string[];
    documentId: string;
    latestRevision: string;
    offerAttachments: any[];
    printType: string;
    mailText: string;
}
